import { useEffect, useMemo, useState } from "react";
import { Boxes, Loader2, Plus, Save, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { fetchAdminBundle, fetchAdminCatalogue, saveAdminBundle } from "@/lib/api";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function BundleContentsEditor({ productRecordId, callerUserRecordId, price, onSaved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [recalc, setRecalc] = useState(false);
  const [q, setQ] = useState("");
  const [catalogue, setCatalogue] = useState([]);

  useEffect(() => {
    if (!productRecordId || !callerUserRecordId) return;
    setLoading(true);
    Promise.all([
      fetchAdminBundle({ callerUserRecordId, productRecordId }),
      fetchAdminCatalogue({ callerUserRecordId, includeInactive: 0 }),
    ])
      .then(([b, c]) => { setItems(b.items || []); setCatalogue(c.products || []); })
      .catch(() => toast.error("Could not load bundle contents"))
      .finally(() => setLoading(false));
  }, [productRecordId, callerUserRecordId]);

  const inBundle = useMemo(() => new Set(items.map((i) => i.id)), [items]);
  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return catalogue.filter((p) => p.id !== productRecordId && !inBundle.has(p.id) && p.name.toLowerCase().includes(s)).slice(0, 8);
  }, [q, catalogue, inBundle, productRecordId]);

  const componentsTotal = items.reduce((a, i) => a + Number(i.price || 0), 0);

  function add(p) {
    setItems((l) => [...l, { id: p.id, name: p.name, image_url: p.image_url, excerpt: p.excerpt, price: p.price }]);
    setQ("");
    setDirty(true);
  }
  function remove(id) {
    setItems((l) => l.filter((i) => i.id !== id));
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try {
      const res = await saveAdminBundle({ callerUserRecordId, productRecordId, productRecordIds: items.map((i) => i.id), recalcPrice: recalc });
      setItems(res.items || []);
      setDirty(false);
      toast.success(`Bundle saved · ${res.items?.length || 0} products${recalc ? ` · price ${fmt(res.price)}` : ""}`);
      onSaved?.(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="pt-2 border-t border-zinc-200" data-testid="bundle-editor">
      <div className="flex items-center gap-2 mb-1">
        <Boxes size={15} className="text-violet-600" />
        <h3 className="text-sm font-bold uppercase tracking-widest text-violet-700">Bundle contents</h3>
        <span className="text-[10px] text-zinc-500 ml-auto" data-testid="bundle-editor-count">{items.length} products · {fmt(componentsTotal)} separately</span>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">What's listed on the storefront bundle page and linked on the Airtable product card.</p>

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="animate-spin text-zinc-400" size={18} /></div>
      ) : (
        <>
          <ul className="divide-y divide-zinc-100 rounded-xl border border-zinc-200 bg-white" data-testid="bundle-editor-list">
            {items.length === 0 && <li className="px-3 py-4 text-xs text-zinc-500 text-center">No products in this bundle yet — search below to add.</li>}
            {items.map((it) => (
              <li key={it.id} className="flex items-center gap-2.5 px-2.5 py-2" data-testid={`bundle-editor-item-${it.id}`}>
                <div className="h-9 w-9 rounded-lg bg-zinc-50 border border-zinc-200 overflow-hidden shrink-0">
                  {it.image_url && <img src={it.image_url} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-zinc-900 truncate">{it.name}</div>
                  <div className="text-[11px] text-zinc-500">{fmt(it.price)}</div>
                </div>
                <button type="button" onClick={() => remove(it.id)} className="p-1.5 rounded-lg text-zinc-400 hover:text-rose-600 hover:bg-rose-50" aria-label={`Remove ${it.name}`} data-testid={`bundle-editor-remove-${it.id}`}>
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>

          <div className="relative mt-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Add a product to the bundle…" className="pl-8 h-10 rounded-xl" data-testid="bundle-editor-search" />
            {matches.length > 0 && (
              <ul className="absolute z-20 left-0 right-0 mt-1 rounded-xl border border-zinc-200 bg-white shadow-lg overflow-hidden" data-testid="bundle-editor-results">
                {matches.map((p) => (
                  <li key={p.id}>
                    <button type="button" onClick={() => add(p)} className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-violet-50" data-testid={`bundle-editor-add-${p.id}`}>
                      <div className="h-8 w-8 rounded-md bg-zinc-50 border border-zinc-200 overflow-hidden shrink-0">
                        {p.image_url && <img src={p.image_url} alt="" className="h-full w-full object-cover" />}
                      </div>
                      <span className="text-sm text-zinc-800 flex-1 truncate">{p.name}</span>
                      <span className="text-[11px] text-zinc-500">{fmt(p.price)}</span>
                      <Plus size={14} className="text-violet-600" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 mt-3">
            <Label className="flex items-center gap-2 text-xs text-zinc-700 cursor-pointer">
              <input type="checkbox" checked={recalc} onChange={(e) => setRecalc(e.target.checked)} className="h-4 w-4 accent-violet-600" data-testid="bundle-editor-recalc" />
              Re-price bundle to {fmt(componentsTotal)} {price != null && <span className="text-zinc-400">(now {fmt(price)})</span>}
            </Label>
            <Button onClick={save} disabled={saving || !dirty && !recalc} className="h-9 rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="bundle-editor-save">
              {saving ? <Loader2 className="animate-spin" size={14} /> : <><Save size={14} className="mr-1.5" /> Save bundle</>}
            </Button>
          </div>
        </>
      )}
    </section>
  );
}
